import { useMemo, useState } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { Testimonial } from "@/mocks/testimonials";
import ReviewCard from "./ReviewCard";

export default function ReviewsList({ reviews }: { reviews: Testimonial[] }) {
  const options = useMemo(
    () => ["All", ...Array.from(new Set(reviews.map((item) => item.experience)))],
    [reviews],
  );
  const [active, setActive] = useState("All");

  const filtered = useMemo(
    () => (active === "All" ? reviews : reviews.filter((item) => item.experience === active)),
    [active, reviews],
  );

  return (
    <section id="all-reviews" className="sun-glaze bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-8 md:mb-10">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              All Reviews
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Read What Visitors Say
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Filter by the experience you are curious about, and read every word and view every photo
            they shared with us.
          </p>
        </Reveal>

        <Reveal className="mb-10">
          <div className="flex flex-wrap gap-2">
            {options.map((option) => {
              const isActive = option === active;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => setActive(option)}
                  className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-secondary-500 border-secondary-500 text-background-50"
                      : "bg-background-100 border-background-300 text-foreground-700 hover:border-secondary-300 hover:text-secondary-700"
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {filtered.map((review, index) => (
            <Reveal key={review.name} delay={(index % 2) * 80}>
              <ReviewCard review={review} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}